import * as React from 'react';
import {useHistory} from 'react-router-dom';
import {
  Box,
  Button,
  Cluster,
  Click,
  Cover,
  Expand,
  Heading,
  Icon,
  Sidebar,
  Template,
  Spinner,
} from '@pluto-tv/assemble';
import {useAppPermissions} from 'app/permissions';
import hubRoutes from 'routes/programming.routes';
import useToggleSearchBarOnSlash from 'helpers/useToggleSearchBarOnSlash';
import HubConfigListTable from 'components/hubList/components/HubConfigListTable';
import {IHubConfigListProps} from 'components/hubList/index';
import HubSearch, {hubConfigTitleId} from 'components/hubList/components/HubSearch';
import {IHubConfigSearch} from 'models/hubConfigs';
import {useHubTableList} from 'components/hubList/hooks/useHubTableList';
import {TSortDirection} from 'models/generic';
import {useHubSearchlLazyLoadProvider} from 'components/hubList/providers/HubSearchLazyLoadProvider';
import {useHubLazyLoadProvider} from 'components/hubList/providers/HubLazyLoadProvider';
import {useUserRegions} from 'helpers/useUserRegions';
import CrudError from 'components/crudError';

const HubCreateForm = React.lazy(() => import('components/hubList/components/CreateHubForm'));

const INIT_PARAMS = {
  sortBy: 'createdAt',
  sortDirection: 'dsc' as TSortDirection,
} as IHubConfigSearch;

const HubConfigList = React.memo(
  ({
    actionsCol = true,
    addNewHubConfig = true,
    inModal = false,
    //showFavoriteSearch = true,
    isSearchExpanded = false,
  }: IHubConfigListProps) => {
    const history = useHistory();
    const {ableTo} = useAppPermissions();

    const canCreate = ableTo('HUB_CREATE');

    const [searchExpanded, setSearchExpanded] = React.useState(isSearchExpanded);
    const [isSearchActive, setIsSearchActive] = React.useState(false);
    const [hubParams, setHubParams] = React.useState<IHubConfigSearch>();

    const {reset: searchReset, search: searchHubs} = useHubSearchlLazyLoadProvider();
    const {search: fetchHubsOnClear} = useHubLazyLoadProvider();
    const {
      search: fetchHubs,
      totalCount: searchTotalCount,
      items: hubConfigItems,
      lazyLoad,
      isLazyLoading,
      isError,
      isLoading,
      searchParams,
      error,
    } = useHubTableList(isSearchActive);
    const {
      activeRegions,
      error: userRegionsError,
      isError: isUserRegionsError,
      isFetching: isUserRegionsFetching,
    } = useUserRegions();

    useToggleSearchBarOnSlash(setSearchExpanded, searchExpanded);

    React.useEffect(() => {
      if (searchExpanded) {
        const hubTitleInput = document.getElementById(hubConfigTitleId);
        hubTitleInput?.focus({
          preventScroll: true,
        });
      }
    }, [searchExpanded]);

    const handleGetHubsConfig = (params: IHubConfigSearch) => {
      setHubParams(params);
      fetchHubs(params);
    };

    React.useEffect(() => {
      handleGetHubsConfig(INIT_PARAMS);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEdit = (id: string) => {
      history.push(hubRoutes.paths.hubEditPreviewPage.replace(':id', id));
    };

    const [createOpen, setCreateOpen] = React.useState(false);

    const openCreate = () => {
      setCreateOpen(true);
    };

    const handleSearch = (search: IHubConfigSearch) => {
      const newParams = {...INIT_PARAMS, ...search};

      searchHubs(newParams);
      setHubParams(newParams);
      setIsSearchActive(true);
    };

    const handleClear = () => {
      fetchHubsOnClear(INIT_PARAMS);
      setHubParams(INIT_PARAMS);

      if (!isSearchActive) return;
      setIsSearchActive(false);
      searchReset(true);
    };

    const handleSort = (search: IHubConfigSearch) => {
      const newParams = {...(hubParams || {}), ...search};
      handleGetHubsConfig(newParams);
    };

    const handleCreate = () => {
      if (!isSearchActive) return;
      const newParams = searchParams || INIT_PARAMS;

      handleGetHubsConfig(newParams);
      searchReset();
    };

    if (isUserRegionsError) {
      const err = userRegionsError;
      return <CrudError error={err} />;
    }

    if (isUserRegionsFetching) {
      return (
        <Box fullHeight={true}>
          <Spinner center={true} minHeight='9.375rem' size='xlarge' />
        </Box>
      );
    }

    if (isError && (error as any)?.status !== 404) {
      return <CrudError error={error} />;
    }

    return (
      <Sidebar fullHeight={true}>
        <Expand width='18.75rem' height='100%' fullHeightContainer={true} isExpanded={searchExpanded}>
          <Template label='expandable'>
            <Box
              background='pewter'
              paddingY={inModal ? 'none' : 'medium'}
              paddingRight='medium'
              paddingLeft={inModal ? 'none' : 'medium'}
              fullHeight={true}
            >
              <HubSearch
                activeRegions={activeRegions}
                setIsExpanded={setSearchExpanded}
                onSearch={handleSearch}
                onClear={handleClear}
              />
            </Box>
          </Template>
        </Expand>
        <Cover
          scrolling={true}
          gutter='large'
          coverTemplateHeight='100%'
          overflow='auto'
          padding={inModal ? 'none' : {mobile: 'medium', wide: 'large'}}
        >
          <Template label='header'>
            <Cluster justify='space-between' align='center' space='medium'>
              <Box width='300px'>
                <Cluster align='end' space='small'>
                  <Heading level='h1'>Hubs</Heading>
                  <Cluster space='xxsmall' align='center'>
                    <Icon
                      icon='tune'
                      id='expandFilters'
                      space='xxxsmall'
                      verticalAlign='bottom'
                      lineHeight='0px'
                      onClick={() => setSearchExpanded(!searchExpanded)}
                    >
                      {searchTotalCount} Items
                    </Icon>
                  </Cluster>
                </Cluster>
              </Box>
              <div style={{position: 'relative', marginTop: '0.625rem', marginBottom: '-1.625rem', zIndex: 9}}>
                <Cluster space='xxlarge'>
                  <Click
                    id='hubConfigTab'
                    paddingBottom='xlarge'
                    borderBottom={true}
                    borderSize='0.125rem'
                    borderStyle='solid'
                    borderColor='primary'
                    color='white'
                    hoverColor='white'
                    size='large'
                    onClick={() => history.push(hubRoutes.paths.hubManagerPage)}
                  >
                    Hub Configurations
                  </Click>
                  <Click
                    id='carouselConfigTab'
                    paddingBottom='xxlarge'
                    borderBottom={false}
                    borderSize='0.125rem'
                    borderStyle='solid'
                    borderColor='primary'
                    color='dust'
                    hoverColor='primary'
                    size='large'
                    onClick={() => history.push(hubRoutes.paths.carouselListPage)}
                  >
                    Carousel Configurations
                  </Click>
                </Cluster>
              </div>
              <Box width='200px'>
                <Cluster space='small' align='center' justify='end'>
                  {addNewHubConfig ? (
                    <>
                      <Button
                        id='addHubConfig'
                        type='primary'
                        onClick={() => openCreate()}
                        permission={canCreate ? '' : 'hidden'}
                      >
                        + New Hub Config
                      </Button>

                      {createOpen && (
                        <React.Suspense fallback={<React.Fragment />}>
                          <HubCreateForm setCreateOpen={setCreateOpen} onCreate={handleCreate} canCreate={canCreate} />
                        </React.Suspense>
                      )}
                    </>
                  ) : (
                    <Box height='1.7rem'></Box>
                  )}
                </Cluster>
              </Box>
            </Cluster>
          </Template>
          <Template label='cover'>
            <Box
              background='pewter'
              borderTop={true}
              borderSize='2px'
              borderColor='cavern'
              paddingTop={inModal ? 'none' : 'xsmall'}
              paddingBottom='none'
              paddingX={inModal ? 'none' : 'large'}
              fullHeight={true}
            >
              <HubConfigListTable
                onEdit={handleEdit}
                actionsCol={actionsCol}
                hubConfigItems={hubConfigItems}
                lazyLoad={lazyLoad}
                isLazyLoading={isLazyLoading}
                isError={isError}
                isLoading={isLoading}
                searchParams={hubParams}
                searchHubs={handleSort}
              />
            </Box>
          </Template>
        </Cover>
      </Sidebar>
    );
  },
);

HubConfigList.displayName = 'HubList';
export default HubConfigList;
